import { generateUUID, calculateDurationInDays, safeFormatDate, calculateActivityDateTime } from './utils';
import { DEFAULT_CURRENCY } from './constants';
import i18n from '../i18n/config';

/**
 * Get the icon class name for an activity type
 * @param {string|Object} typeOrActivity - Activity type string or activity object
 * @returns {string} Bootstrap icon class name
 */
export function getActivityIcon(typeOrActivity) {
  let type;
  
  // Handle both string type and activity object
  if (typeof typeOrActivity === 'string') {
    type = typeOrActivity;
  } else if (typeOrActivity && typeOrActivity.type) {
    type = typeOrActivity.type;
  } else {
    return 'bi-calendar-event'; // Default icon
  }
  
  switch (type) {
    case 'accommodation':
      return 'bi-house-door';
    case 'transportation':
      return 'route-icon';
    case 'attraction':
      return 'bi-geo-alt';
    
    default:
      return 'bi-calendar-event';
  }
}

/**
 * Get activity style information (icon, background color, text color)
 * @param {string|Object} typeOrActivity - Activity type string or activity object
 * @param {Object} options - Optional configuration
 * @returns {Object} Object with icon, bg, and optionally color properties
 */
export function getActivityStyle(typeOrActivity, options = {}) {
  let type;
  
  // Handle both string type and activity object
  if (typeof typeOrActivity === 'string') {
    type = typeOrActivity;
  } else if (typeOrActivity && typeOrActivity.type) {
    type = typeOrActivity.type;
  } else {
    return {
      bg: 'secondary',
      icon: 'bi-calendar-event',
      color: '#6c757d'
    };
  }
  
  // Transportation - use a single generic icon for all types
  if (type === 'transportation') {
    return { 
      bg: 'info',
      icon: 'route-icon',
      color: '#0dcaf0'
    };
  }
  
  // Other activity types
  switch (type) {
    case 'accommodation':
      return { 
        bg: 'success',
        icon: 'bi-house-door',
        color: '#198754'
      };    
    case 'attraction':
      return { 
        bg: 'primary',
        icon: 'bi-geo-alt',
        color: '#0d6efd'
      };
    case 'Destination':
      return { 
        bg: 'primary',
        icon: 'bi-geo-alt-fill',
        color: '#0d6efd'
      };
    case 'Day Location':
      return { 
        bg: 'success',
        icon: 'bi-calendar-day',
        color: '#198754'
      };
    default:
      return { 
        bg: 'secondary',
        icon: 'bi-calendar-event',
        color: '#6c757d'
      };
  }
}

/**
 * Create a base activity object with common properties
 * @param {Object} params - Activity parameters
 * @returns {Object} - New base activity object
 */
export const createBaseActivity = (params = {}) => {
  const {
    id = generateUUID(),
    type = 'activity',
    name = '',
    nameInEnglish = '',
    description = '',
    notes = '',
    startDateTime = '',
    endDateTime = '',
    cost = {
      value: '',
      currency: DEFAULT_CURRENCY
    },
    links = [],
    images = [],
    completed = false,
    option = [], // Can be array [1,2,3], "all", or [] (empty array defaults to option 1)
    descriptionAlign = 'auto'
  } = params;
  
  
  return {
    id,
    type,
    name,
    nameInEnglish,
    description,
    notes,
    startDateTime,
    endDateTime,
    cost,
    links,
    images,
    completed,
    option, // Preserve option field (empty array means option 1 by default)
    descriptionAlign
  };
};

/**
 * Creates a new accommodation activity
 * @param {Object} params - Activity parameters
 * @returns {Object} - New accommodation activity
 */
export const createAccommodationActivity = (params = {}) => {
  const baseActivity = createBaseActivity({
    ...params,
    type: 'accommodation'
  });

  // Add accommodation-specific properties
  return {
    ...baseActivity,
    checkInTime: params.checkInTime || '',
    checkOutTime: params.checkOutTime || '',
    location: params.location || { address: '', latitude: '', longitude: '' }
  };
};

/**
 * Creates a new transportation activity
 * @param {Object} params - Activity parameters
 * @returns {Object} - New transportation activity
 */
export const createTransportationActivity = (params = {}) => {
  const baseActivity = createBaseActivity({
    ...params,
    type: 'transportation'
  });

  return {
    ...baseActivity,    
    type: 'transportation',
    fromLocation: params.fromLocation || { address: '', latitude: '', longitude: '' },
    toLocation: params.toLocation ||  { address: '', latitude: '', longitude: '' },
    estimatedDuration: params.estimatedDuration || '' // Also preserve duration
  };
};

/**
 * Creates a new attraction activity
 * @param {Object} params - Activity parameters
 * @returns {Object} - New attraction activity
 */
export const createAttractionActivity = (params = {}) => {
  const baseActivity = createBaseActivity({
    ...params,
    type: 'attraction'    
  });

  return {
    ...baseActivity,
    location: params.location || { address: '', addressInEnglish: '', latitude: '', longitude: '' },
    estimatedDuration: params.estimatedDuration || '' // Also preserve duration
  };
};

/**
 * Creates the appropriate activity based on type
 * @param {Object} params - Activity parameters
 * @returns {Object} - New activity of the specified type
 */
export const createActivityByType = (params = {}) => {
  const type = params.type || 'activity';
  
  switch (type) {
    case 'accommodation':
      return createAccommodationActivity(params);
    case 'transportation':
      return createTransportationActivity(params);
    case 'attraction':
      return createAttractionActivity(params);
    default:
      return createBaseActivity(params);
  }
};

/**
 * Helper function to add Google Maps links to an activity
 * This is the ONLY place where Google Maps links should be added
 * @param {Object} activity - Activity object
 * @param {string} activityType - Type of activity ('accommodation', 'attraction', 'transportation')
 * @returns {Object} - Activity with Google Maps links added
 */
const addGoogleMapsLinksToActivity = (activity, activityType) => {
  // Initialize links array if needed
  if (!activity.links) {
    activity.links = [];
  }
  
  // For accommodation and attraction: add single "Google Map" link
  if ((activityType === 'accommodation' || activityType === 'attraction') && activity.name) {
    const loc = activity.location;
    // Use English name if available, otherwise use regular name
    let searchQuery = activity.nameInEnglish || activity.name;
    if (loc && typeof loc === 'object' && (loc.cityInEnglish || loc.city || loc.countryInEnglish || loc.country)) {
      const parts = [];
      // Use English city/country if available
      if (loc.cityInEnglish || loc.city) parts.push(loc.cityInEnglish || loc.city);
      if (loc.countryInEnglish || loc.country) parts.push(loc.countryInEnglish || loc.country);
      searchQuery += `, ${parts.join(', ')}`;
    }
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`;
    // Check if Google Maps link already exists
    const googleMapName = i18n.t('planDetail.activityModals.googleMap', { defaultValue: 'Google Map' });
    const hasGoogleMapsLink = activity.links.some(link => 
      link.name === 'Google Map' || 
      link.name === 'google map' || 
      link.name === googleMapName
    );
    if (!hasGoogleMapsLink) {
      activity.links.push({
        name: googleMapName,
        url: googleMapsUrl,
        isPrivate: false
      });
    }
  }
  
  // For transportation: add "Google Map From" and "Google Map To" links
  if (activityType === 'transportation') {
    // Add "Google Map From" link
    if (activity.fromLocation) {
      const fromLoc = activity.fromLocation;
      // Use English address if available
      let fromSearchQuery = fromLoc.addressInEnglish || fromLoc.address || '';
      if (fromLoc && typeof fromLoc === 'object' && (fromLoc.cityInEnglish || fromLoc.city || fromLoc.countryInEnglish || fromLoc.country)) {
        const parts = [];
        // Use English fields if available
        if (fromLoc.addressInEnglish || fromLoc.address) parts.push(fromLoc.addressInEnglish || fromLoc.address);
        if (fromLoc.cityInEnglish || fromLoc.city) parts.push(fromLoc.cityInEnglish || fromLoc.city);
        if (fromLoc.countryInEnglish || fromLoc.country) parts.push(fromLoc.countryInEnglish || fromLoc.country);
        fromSearchQuery = parts.join(', ');
      }
      if (fromSearchQuery && fromSearchQuery.trim()) {
        const googleMapsFromUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fromSearchQuery)}`;
        const googleMapFromName = i18n.t('planDetail.activityModals.googleMapFrom', { defaultValue: 'Google Map From' });
        const hasGoogleMapsFromLink = activity.links.some(link => 
          link.name === 'Google Map From' || 
          link.name === 'google map from' || 
          link.name === googleMapFromName
        );
        if (!hasGoogleMapsFromLink) {
          activity.links.push({
            name: googleMapFromName,
            url: googleMapsFromUrl,
            isPrivate: false
          });
        }
      }
    }
    
    // Add "Google Map To" link
    if (activity.toLocation) {
      const toLoc = activity.toLocation;
      // Use English address if available
      let toSearchQuery = toLoc.addressInEnglish || toLoc.address || '';
      if (toLoc && typeof toLoc === 'object' && (toLoc.cityInEnglish || toLoc.city || toLoc.countryInEnglish || toLoc.country)) {
        const parts = [];
        // Use English fields if available
        if (toLoc.addressInEnglish || toLoc.address) parts.push(toLoc.addressInEnglish || toLoc.address);
        if (toLoc.cityInEnglish || toLoc.city) parts.push(toLoc.cityInEnglish || toLoc.city);
        if (toLoc.countryInEnglish || toLoc.country) parts.push(toLoc.countryInEnglish || toLoc.country);
        toSearchQuery = parts.join(', ');
      }
      if (toSearchQuery && toSearchQuery.trim()) {
        const googleMapsToUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(toSearchQuery)}`;
        const googleMapToName = i18n.t('planDetail.activityModals.googleMapTo', { defaultValue: 'Google Map To' });
        const hasGoogleMapsToLink = activity.links.some(link => 
          link.name === 'Google Map To' || 
          link.name === 'google map to' || 
          link.name === googleMapToName
        );
        if (!hasGoogleMapsToLink) {
          activity.links.push({
            name: googleMapToName,
            url: googleMapsToUrl,
            isPrivate: false
          });
        }
      }
    }
  }
  
  return activity;
};

/**
 * Parse estimatedDuration string to minutes
 * @param {string} estimatedDuration - Duration string like "2 hours" or "30 minutes"
 * @returns {number} Duration in minutes (default: 120)
 */
function parseDurationToMinutes(estimatedDuration) {
  if (!estimatedDuration) return 120;
  
  if (estimatedDuration.includes('hour')) {
    const hoursMatch = estimatedDuration.match(/(\d+(\.\d+)?)-?(\d+(\.\d+)?)?/);
    if (hoursMatch) {
      // If range like "2-3 hours", take the average
      if (hoursMatch[3]) {
        return Math.round(((parseFloat(hoursMatch[1]) + parseFloat(hoursMatch[3])) / 2) * 60);
      } else {
        return Math.round(parseFloat(hoursMatch[1]) * 60);
      }
    }
  } else if (estimatedDuration.includes('minute')) {
    const minutesMatch = estimatedDuration.match(/(\d+)/);
    if (minutesMatch) {
      return parseInt(minutesMatch[1]);
    }
  }
  
  return 120; // Default 2 hours
}

/**
 * Calculate smart start and end datetime for an activity
 * If date doesn't have time component, calculates smart times based on existing activities
 * @param {string} startDate - Start date string (may or may not have time)
 * @param {string} endDate - End date string (may or may not have time)
 * @param {Object} targetDay - Target day object with date and activities
 * @param {string} estimatedDuration - Duration string for calculation
 * @returns {Object} Object with startDateTime and endDateTime
 */
function calculateSmartActivityTimes(startDate, endDate, targetDay, estimatedDuration) {
  let startDateTime = startDate || "";
  let endDateTime = endDate || "";
  
  // If date doesn't have time (no 'T'), calculate smart times
  if (startDateTime && !startDateTime.includes('T')) {
    // Include ALL activities (including accommodations) when finding previous activity
    const existingActivities = targetDay.activities.filter(a => a.endDateTime);
    const durationMinutes = parseDurationToMinutes(estimatedDuration);
    const calculatedTimes = calculateActivityDateTime(targetDay.date, existingActivities, durationMinutes);
    startDateTime = calculatedTimes.startDateTime;
    endDateTime = calculatedTimes.endDateTime;
  }
  
  return { startDateTime, endDateTime };
}

/**
 * UNIFIED function to create and normalize activities
 * This is the ONLY place where activities should be created from AI or external sources
 * Based on the logic from handleAddGeneratedActivity in PlanDetail.js
 * 
 * @param {Object} params - Activity parameters
 * @param {Object} options - Options for creation
 * @param {boolean} options.addGoogleMapsLinks - Whether to auto-add Google Maps links (default: true)
 * @param {boolean} options.includeEstimatedDuration - Whether to include estimatedDuration (default: false)
 * @returns {Object} - Normalized activity object
 */
export const createNormalizedActivity = (params = {}, options = {}) => {
  const {
    addGoogleMapsLinks = true,
    includeEstimatedDuration = false
  } = options;
  
  // Extract activity type
  const activityType = params.type || 'attraction';
  
  // Prepare links array from existing links and website
  const links = [];
  
  // Add website to links if available
  if (params.website) {
    links.push({
      name: i18n.t('planDetail.activityModals.website', { defaultValue: 'Website' }),
      url: params.website,
      isPrivate: false // Automatically created links are public
    });
  }
  
  // Add existing links if they exist
  if (params.links && Array.isArray(params.links)) {
    params.links.forEach(link => {
      if (link.url && link.name) {
        links.push({
          name: link.name,
          url: link.url,
          isPrivate: false // All AI-generated/automatically created links are public
        });
      }
    });
  }
  
  // Prepare notes - combine notes and price
  let notes = params.notes || '';
  if (params.price && params.price.trim() !== '') {
    if (notes) {
      notes += '\n\nPrice: ' + params.price;
    } else {
      notes = 'Price: ' + params.price;
    }
  }
  
  // Create activity params without estimatedDuration (unless explicitly included)
  const activityParams = {
    ...params,
    links: links,
    notes: notes
  };
  
  // Remove estimatedDuration unless explicitly included
  if (!includeEstimatedDuration) {
    delete activityParams.estimatedDuration;
  }
  
  // Create the activity using the type-specific creator
  let activity = createActivityByType(activityParams);
  
  // Add Google Maps links if requested
  if (addGoogleMapsLinks) {
    activity = addGoogleMapsLinksToActivity(activity, activityType);
  }
  
  return activity;
};

/**
 * Converts AI-generated trip result to a structured plan format
 * @param {Object} aiResult - AI result JSON with trip details
 * @returns {Object} - Structured plan object ready to be saved
 */
export const convertAiResultToPlan = (aiResult) => {
  if (!aiResult) {
    throw new Error('No AI result provided');
  }

  // Create base plan data structure
  const planData = {
    id: generateUUID(),
    name: aiResult.name,
    nameInEnglish: aiResult.nameInEnglish || null,
    description: aiResult.description,
    groupInfo: aiResult.groupInfo,
    currency: DEFAULT_CURRENCY, // Default currency
    startDate: aiResult.startDate,
    endDate: aiResult.endDate,
    days: [],
    additionalCosts: [], // Add empty additionalCosts array
    todos: [  // Add default todos
      {
        id: generateUUID(),
        text: i18n.t('planDetail.todo.defaultTodos.travelInsurance'),
        createdAt: new Date().toISOString(),
        status: 'pending',
        source: 'plan'
      },
      {
        id: generateUUID(),
        text: i18n.t('planDetail.todo.defaultTodos.checkInOutbound'),
        createdAt: new Date().toISOString(),
        status: 'pending',
        source: 'plan'
      },
      {
        id: generateUUID(),
        text: i18n.t('planDetail.todo.defaultTodos.checkInInbound'),
        createdAt: new Date().toISOString(),
        status: 'pending',
        source: 'plan'
      }
    ]
  };
  
  // Preserve destination images if they exist
  if (aiResult.images && aiResult.images.length > 0) {
    planData.images = [...aiResult.images];
    
    // Also preserve provider information if available
    if (aiResult.imagesProvider) {
      planData.imagesProvider = aiResult.imagesProvider;
    }
  }
  
  // Also check for images in the destination object
  if (aiResult.destination && aiResult.destination.images && aiResult.destination.images.length > 0) {
    // If we already have images from the top level, merge them
    if (planData.images) {
      planData.images = [...planData.images, ...aiResult.destination.images];
    } else {
      planData.images = [...aiResult.destination.images];
    }
  }
  
  // Add days based on the start and end dates
  if (aiResult.startDate && aiResult.endDate) {
    const startDate = new Date( safeFormatDate(aiResult.startDate, 'yyyy-MM-dd' ));
    const endDate = new Date( safeFormatDate(aiResult.endDate, 'yyyy-MM-dd' ));
    
    // Use calculateDurationInDays utility instead of manual calculation
    const diffDays = calculateDurationInDays(startDate, endDate) + 1; // Add 1 to include end date
    
    
    // Create initial days structure (empty days without any activities)
    for (let i = 0; i < diffDays; i++) {
      const currentDate = new Date(aiResult.startDate);  
      currentDate.setDate(currentDate.getDate() + i);
      
      // Format using safeFormatDate with debugging      
      const formattedDate = safeFormatDate(currentDate.toISOString(), 'yyyy-MM-dd');      
      const formattedDayId = `day-${formattedDate}`;
      
      planData.days.push({
        id: formattedDayId,
        date: formattedDate,
        activities: []        
      });
    }
    
    // Process accommodations and add them as activities
    if (aiResult.Accommodations && aiResult.Accommodations.length > 0) {
      aiResult.Accommodations.forEach(accommodation => {
        if (accommodation.startDate && accommodation.endDate) {
          // Format using safeFormatDate with debugging
          console.log('Accommodation start date input:', accommodation.startDate);
          const formattedDate = safeFormatDate(accommodation.startDate, 'yyyy-MM-dd');
          console.log('Accommodation formatted date:', formattedDate);
          const accomDayId = `day-${formattedDate}`;
          
          // Find the day matching the accommodation's start date
          const matchingDayIndex = planData.days.findIndex(day => day.id === accomDayId);
          console.log('Matching day index for accommodation:', matchingDayIndex, 'Looking for:', accomDayId);
          
          if (matchingDayIndex >= 0) {
            const targetDay = planData.days[matchingDayIndex];
            const { startDateTime, endDateTime } = calculateSmartActivityTimes(
              accommodation.startDate,
              accommodation.endDate,
              targetDay,
              accommodation.estimatedDuration
            );
            
            // Create accommodation activity using unified function
            const accommodationActivity = createNormalizedActivity({
              type: 'accommodation',
              name: accommodation.name,
              nameInEnglish: accommodation.nameInEnglish || null,
              description: accommodation.description || "",
              startDateTime: startDateTime,
              endDateTime: endDateTime,
              location: accommodation.location || "",
              website: accommodation.website,
              links: accommodation.links || [],
              checkInTime: accommodation.checkInTime || "",
              checkOutTime: accommodation.checkOutTime || "",
              images: accommodation.images || []
            }, {
              addGoogleMapsLinks: true,
              includeEstimatedDuration: false
            });
            
            targetDay.activities.push(accommodationActivity);
          }
        }
      });
    }
    
    // Process transportation and add them as activities
    if (aiResult.Transportations && aiResult.Transportations.length > 0) {
      aiResult.Transportations.forEach(transport => {
        // Try to figure out which day this transportation belongs to
        let targetDay = null;
        
        if (transport.startDate) {
          // Format with safeFormatDate with debugging
          console.log('Transport start date input:', transport.startDate);
          const transportDateFormatted = safeFormatDate(transport.startDate, 'yyyy-MM-dd');
          console.log('Transport formatted date:', transportDateFormatted);
          targetDay = planData.days.find(day => day.date === transportDateFormatted);
          console.log('Found matching day for transport:', targetDay ? targetDay.id : 'None');
        }
        
        if (targetDay) {
          const { startDateTime, endDateTime } = calculateSmartActivityTimes(
            transport.startDate,
            transport.endDate,
            targetDay,
            transport.estimatedDuration
          );
          
          // Create transportation activity using unified function
          const transportationActivity = createNormalizedActivity({
            type: 'transportation',
            name: transport.name || "Transportation",
            nameInEnglish: transport.nameInEnglish || null,
            description: transport.description || "",
            startDateTime: startDateTime,
            endDateTime: endDateTime,
            fromLocation: transport.fromLocation || { address: "", latitude: "", longitude: "" },
            toLocation: transport.toLocation || { address: "", latitude: "", longitude: "" },
            links: transport.links || []
          }, {
            addGoogleMapsLinks: true,
            includeEstimatedDuration: false
          });
          
          targetDay.activities.push(transportationActivity);
        }
      });
    }
    
    // Process user-entered attractions and add them as activities
    if (aiResult.userAttractions && aiResult.userAttractions.length > 0) {
      console.log('Processing user attractions:', aiResult.userAttractions);
      
      // Track attractions without dates to add to suggested attractions
      const attractionsWithoutDates = [];
      
      aiResult.userAttractions.forEach(attraction => {
        // Try to figure out which day this attraction belongs to
        let targetDay = null;
        
        if (attraction.startDate) {
          console.log('Attraction start date input:', attraction.startDate);
          const attractionDateFormatted = safeFormatDate(attraction.startDate, 'yyyy-MM-dd');
          console.log('Attraction formatted date:', attractionDateFormatted);
          targetDay = planData.days.find(day => day.date === attractionDateFormatted);
          console.log('Found matching day for attraction:', targetDay ? targetDay.id : 'None');
        }
        
        if (targetDay) {
          const { startDateTime, endDateTime } = calculateSmartActivityTimes(
            attraction.startDate,
            attraction.endDate,
            targetDay,
            attraction.estimatedDuration
          );
          
          // Create attraction activity using unified function
          const attractionActivity = createNormalizedActivity({
            type: 'attraction',
            name: attraction.name,
            nameInEnglish: attraction.nameInEnglish || null,
            description: attraction.description || "",
            startDateTime: startDateTime,
            endDateTime: endDateTime,
            location: attraction.location || { address: "", latitude: "", longitude: "" },
            website: attraction.website,
            links: attraction.links || [],
            notes: attraction.price || "",
            // Preserve the image data with provider and attribution info
            images: attraction.images || []
          }, {
            addGoogleMapsLinks: true,
            includeEstimatedDuration: false
          });
          
          targetDay.activities.push(attractionActivity);
        } else {
          // No matching day found for this attraction, add it to the list to be moved
          attractionsWithoutDates.push(attraction);
        }
      });
      
      // If we found attractions without dates, track them for adding to suggested attractions
      if (attractionsWithoutDates.length > 0) {
        console.log(`Found ${attractionsWithoutDates.length} user attractions without dates, adding to suggested attractions`);
      }
    }
    
    // Note: We do NOT automatically add suggested attractions to days
    // User will add them manually in the plan detail page
    // But we do store them in the plan data for later use
    if (aiResult.suggestedAttractions && aiResult.suggestedAttractions.length > 0) {
      planData.suggestedAttractions = aiResult.suggestedAttractions.map(attraction => ({
        ...attraction,
        // Ensure images are preserved
        images: attraction.images || []
      }));
    } else {
      planData.suggestedAttractions = [];
    }
    
    // Preserve interests if they exist
    if (aiResult.interests && Array.isArray(aiResult.interests)) {
      planData.interests = [...aiResult.interests];
    }
    
    // Add user attractions without dates to suggested attractions
    if (aiResult.userAttractions && aiResult.userAttractions.length > 0) {
      const attractionsWithoutDates = aiResult.userAttractions.filter(attraction => {
        if (!attraction.startDate) return true;
        
        const attractionDateFormatted = safeFormatDate(attraction.startDate, 'yyyy-MM-dd');
        const targetDay = planData.days.find(day => day.date === attractionDateFormatted);
        return !targetDay;
      });
      
      if (attractionsWithoutDates.length > 0) {
        // Add a note that these were user-specified
        const userSpecifiedAttractions = attractionsWithoutDates.map(attraction => ({
          ...attraction,
          userSpecified: true, // Mark as user-specified
          images: attraction.images || []
        }));
        
        // Add to suggestedAttractions array
        planData.suggestedAttractions = [
          ...(planData.suggestedAttractions || []),
          ...userSpecifiedAttractions
        ];
        
        console.log(`Added ${userSpecifiedAttractions.length} user attractions without dates to suggested attractions`);
      }
    }
  }
  
  // Sort activities by time for each day after all activities have been added
  planData.days.forEach(day => {
    if (day.activities && day.activities.length > 0) {
      day.activities = sortActivitiesByTime(day.activities);
    }
  });
  
  return planData;
};

/**
 * Sort activities by their start time
 * Activities without start times are pushed to the end
 * @param {Array} activities - Array of activity objects
 * @returns {Array} - Sorted array of activities (new array, doesn't mutate input)
 */
/**
 * Check if an accommodation spans multiple days
 * @param {Object} activity - Activity object
 * @returns {boolean} True if accommodation spans multiple days
 */
export function isMultiDayAccommodation(activity) {
  return activity.type === 'accommodation' && 
         activity.startDateTime && 
         activity.endDateTime && 
         new Date(activity.startDateTime).toDateString() !== new Date(activity.endDateTime).toDateString();
}

/**
 * Check if a day falls within an accommodation's date range
 * @param {string|Date} dayDate - The day date
 * @param {string|Date} startDateTime - Accommodation start date/time
 * @param {string|Date} endDateTime - Accommodation end date/time
 * @returns {boolean} True if day is within the accommodation's date range
 */
export function isDayWithinAccommodationRange(dayDate, startDateTime, endDateTime) {
  if (!dayDate || !startDateTime || !endDateTime) return false;
  
  const day = new Date(dayDate);
  const start = new Date(startDateTime);
  const end = new Date(endDateTime);
  
  // Reset time part for proper date comparison
  day.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  return day >= start && day <= end;
}

/**
 * Find all accommodations that span multiple days
 * @param {Object} parsedData - Plan data with days array
 * @returns {Array} Array of multi-day accommodations with dayId and originalDay
 */
export function findMultiDayAccommodations(parsedData) {
  if (!parsedData || !parsedData.days) return [];
  
  const multiDayAccommodations = [];
  
  parsedData.days.forEach(day => {
    if (day.activities) {
      day.activities.forEach(activity => {
        if (isMultiDayAccommodation(activity)) {
          multiDayAccommodations.push({
            ...activity,
            dayId: day.id,
            originalDay: day.date
          });
        }
      });
    }
  });
  
  return multiDayAccommodations;
}

export const sortActivitiesByTime = (activities) => {
  if (!activities || !Array.isArray(activities)) return [];
  
  return [...activities].sort((a, b) => {
    // If no start times, maintain original order
    if (!a.startDateTime && !b.startDateTime) return 0;
    if (!a.startDateTime) return 1; // Push items without start time to the end
    if (!b.startDateTime) return -1;
    
    // Extract time parts and compare as strings
    const timeA = a.startDateTime.split('T')[1]?.substring(0, 5) || ''; // Get HH:MM
    const timeB = b.startDateTime.split('T')[1]?.substring(0, 5) || '';
    
    return timeA.localeCompare(timeB);
  });
};

/**
 * Toggle an option in the activity's option array
 * @param {number|string|Array|string} currentOption - Current option value (can be number, array, 'all', or empty/null)
 * @param {number|string} optionValue - The option number to toggle (1, 2, or 3)
 * @returns {Array} New option array after toggling
 */
export function toggleOption(currentOption, optionValue) {
  const optionNum = parseInt(optionValue);
  
  // Get current options - if empty/null, start with empty array (not all options)
  const currentOptions = currentOption === 'all' 
    ? [] // Convert "all" to empty array
    : (Array.isArray(currentOption) ? currentOption : (currentOption ? [currentOption] : []));
  
  let newOption;
  // Toggle the option
  if (currentOptions.includes(optionNum)) {
    // Remove option
    newOption = currentOptions.filter(opt => opt !== optionNum);
    // If no options left, set to empty array (which means all options in logic, but not shown in UI)
    if (newOption.length === 0) {
      newOption = [];
    }
  } else {
    // Add option
    newOption = [...currentOptions, optionNum].sort();
  }
  
  return newOption;
}

/**
 * Check if an option is selected in the activity's option value
 * @param {number|string|Array|string} currentOption - Current option value (can be number, array, 'all', or empty/null)
 * @param {number|string} optionValue - The option number to check (1, 2, or 3)
 * @returns {boolean} True if the option is explicitly selected
 */
export function isOptionSelected(currentOption, optionValue) {
  const optionNum = parseInt(optionValue);
  
  // Only return true if the option is explicitly in the array
  // Empty/null means "all options" in logic, but we don't auto-select them in UI
  if (!currentOption || (Array.isArray(currentOption) && currentOption.length === 0)) {
    return false; // Don't auto-select when empty/null
  }
  if (currentOption === 'all') {
    return false; // Don't auto-select when "all"
  }
  if (Array.isArray(currentOption)) {
    return currentOption.includes(optionNum);
  }
  if (currentOption === optionNum) return true;
  return false;
}

/**
 * Check if an activity matches the selected option filter
 * @param {Object} activity - Activity object to check
 * @param {number|string} selectedOption - Selected option filter ('all', 1, 2, or 3)
 * @returns {boolean} True if the activity matches the filter
 */
export function activityMatchesOption(activity, selectedOption) {
  if (!activity) return false;
  
  // If filter is "all", show all activities
  if (selectedOption === 'all') {
    return true;
  }
  
  const activityOptions = activity.option;
  
  // If activity has no option (empty/null), it means all options, so it matches any filter
  if (!activityOptions || (Array.isArray(activityOptions) && activityOptions.length === 0)) {
    return true;
  }
  
  // If activity option is "all", it matches any filter
  if (activityOptions === 'all') {
    return true;
  }
  
  // Check if the selected option is in the activity's options
  if (Array.isArray(activityOptions)) {
    return activityOptions.includes(selectedOption);
  }
  
  // If activity option is a single number, check if it matches
  if (typeof activityOptions === 'number') {
    return activityOptions === selectedOption;
  }
  
  return false;
} 